// Custom signals: pure helpers for the create flow, the feed row and the tracker. The server owns what a custom
// signal is (see /cm/signals/custom); this file only shapes it for the page.

export const CUSTOM = "custom";
export const MAX_KEYWORDS = 5;
export const MIN_TEXT = 8;
export const MAX_TEXT = 500;

// The four starters when the server's list hasn't loaded (same shape as GET /cm/signals/custom/templates).
export const FALLBACK_TEMPLATES = [
  { key: "account_talks_about", title: "Account talks about [topic]", phrase: "Tell me when a target account talks about [topic]", example: "Tell me when a target account talks about moving off Salesforce", badge: "Deep intel" },
  { key: "hiring_with_tech", title: "Hiring for [roles] using [tech]", phrase: "Tell me when an account is hiring for [roles] using [tech]", example: "Tell me when an account is hiring data engineers using Snowflake", badge: "Deep intel" },
  { key: "prospect_posts_about", title: "Prospect posts about [topic]", phrase: "Tell me when a prospect posts about [topic]", example: "Tell me when a prospect posts about outbound pipeline problems", badge: "Deep intel" },
  { key: "expanding_into", title: "Expanding into [region]", phrase: "Tell me when an account is expanding into [region]", example: "Tell me when an account is expanding into Europe", badge: "Deep intel" }
];

export const EXAMPLES = [
  "Tell me when a target account talks about moving off Salesforce",
  "Tell me when an account is hiring data engineers using Snowflake",
  "Tell me when a prospect posts about outbound pipeline problems"
];

export const isCustomType = type => type === CUSTOM;
/** A custom signal that reads your competitors: agents never run on what competitors do, so it offers none. */
export const watchesCompetitors = row => ((((row && row.custom) || {}).target) || {}).type === "competitors";

export const isCustomRow = row => !!row && isCustomType(((row && row.signal) || {}).signalType);

/** A template phrase as parts: [{text}] and [{slot: "topic"}] for each [placeholder]. */
export const phraseParts = phrase => {
  const out = [];
  const re = /\[([^\]]+)\]/g;
  const s = String(phrase || "");
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ text: s.slice(last, m.index) });
    out.push({ slot: m[1] });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
};

/** The phrase with each slot filled; empty slots keep their [placeholder] so the text says what is missing. */
export const fillPhrase = (phrase, values = {}) =>
  phraseParts(phrase).map(p => (p.slot ? (String(values[p.slot] || "").trim() || `[${p.slot}]`) : p.text)).join("");

export const slotsMissing = (phrase, values = {}) =>
  phraseParts(phrase).filter(p => p.slot && !String(values[p.slot] || "").trim()).map(p => p.slot);

/** Why the ask can't go to the server yet, or "" when it can. */
export const textProblem = text => {
  const t = String(text || "").trim();
  if (t.length < MIN_TEXT) return "Say a little more about what you want to hear about.";
  if (t.length > MAX_TEXT) return `Keep it under ${MAX_TEXT} characters.`;
  return "";
};

/** Adds a keyword: trimmed, no repeats (any case), at most MAX_KEYWORDS. Returns the same list when nothing changes. */
export const addKeyword = (list, word) => {
  const all = Array.isArray(list) ? list : [];
  const w = String(word || "").trim().replace(/\s+/g, " ");
  if (!w || all.length >= MAX_KEYWORDS || all.some(k => k.toLowerCase() === w.toLowerCase())) return all;
  return all.concat(w);
};
export const removeKeyword = (list, word) => (Array.isArray(list) ? list : []).filter(k => k !== word);

const SOURCE_TEXT = {
  company_posts: "their company page",
  employee_posts: "posts by their people",
  person_posts: "their own posts"
};
/** "their company page and posts by their people", from the definition's sources. */
export const sourcesText = sources => {
  const parts = (Array.isArray(sources) ? sources : []).map(s => SOURCE_TEXT[s]).filter(Boolean);
  if (!parts.length) return "";
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};

/** accounts | contacts for a target type; and the sources that go with it. */
export const appliesToOf = targetType => (targetType === "contact_list" ? "contacts" : "accounts");
const sourcesFor = (appliesTo, sources) => {
  if (appliesTo === "contacts") return ["person_posts"];
  const kept = (Array.isArray(sources) ? sources : []).filter(s => s !== "person_posts");
  return kept.length ? kept : ["company_posts", "employee_posts"];
};

/** The definition, matched to who it watches (contacts only read their own posts; accounts read the company's). */
export const definitionFor = (definition, targetType) => {
  const d = definition || {};
  const appliesTo = appliesToOf(targetType);
  return { name: d.name || "", condition: d.condition || "", keywords: Array.isArray(d.keywords) ? d.keywords : [], sources: sourcesFor(appliesTo, d.sources), appliesTo };
};

/** Who step 2 starts on: the draft's suggestedTarget, else "competitors" when the ask mentions them, else "". */
export const suggestedTargetOf = (draft, text) => {
  const t = draft && draft.suggestedTarget;
  if (t === "competitors" || t === "contacts" || t === "accounts") return t;
  return /competitor/i.test(String(text || "")) ? "competitors" : "";
};

/**
 * A record's name for a title: the first real name given, and never a bare lowercase slug or domain
 * ("lambdatest" reads "Lambdatest", "acme-freight.com" reads "Acme Freight").
 */
export const displayName = (...names) => {
  const all = names.map(n => String(n || "").trim()).filter(Boolean);
  const name = all.find(n => n !== n.toLowerCase() || /\s/.test(n)) || all[0] || "";
  if (!name || name !== name.toLowerCase() || /\s/.test(name)) return name;
  const base = name.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\.[a-z]{2,}(\.[a-z]{2})?(\/.*)?$/, "");
  return base.split(/[-_.]+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

export const creditsPerRecord = appliesTo => (appliesTo === "contacts" ? 1 : 2);

/** How often a custom signal checks: [value, label, period]. */
export const FREQUENCIES = [
  ["daily", "Daily", "day"],
  ["weekly", "Weekly", "week"],
  ["monthly", "Monthly", "month"],
  ["custom", "Custom", ""]
];
const daysOf = n => Math.max(1, Math.min(30, Math.round(Number(n)) || 7));
/** "week", or "3 days" for a custom interval. */
export const periodOf = (frequency, everyDays) => {
  if (frequency === "custom") {
    const n = daysOf(everyDays);
    return n === 1 ? "day" : `${n} days`;
  }
  return (FREQUENCIES.find(f => f[0] === frequency) || FREQUENCIES[1])[2];
};

/**
 * The cost line: "2 credits per company each week · about 40 credits a week". `perWeek` is the server's estimate
 * for one check when there is one, else records × the per-record price; without either only the price shows.
 */
export const costLine = ({ appliesTo, records, perWeek, frequency, everyDays } = {}) => {
  const per = creditsPerRecord(appliesTo);
  const unit = appliesTo === "contacts" ? "contact" : "company";
  const period = periodOf(frequency, everyDays);
  const several = /^\d/.test(period); // "3 days": "each check", "every 3 days"
  const price = `${per} credit${per === 1 ? "" : "s"} per ${unit} each ${several ? "check" : period}`;
  const total = perWeek != null ? Number(perWeek) : records != null ? Number(records) * per : null;
  if (total == null || isNaN(total)) return price;
  const line = `${price} · about ${total.toLocaleString()} credit${total === 1 ? "" : "s"} ${several ? "every" : "a"} ${period}`;
  // Any interval but monthly also says what a month comes to, so the choices compare at a glance.
  const days = frequency === "daily" ? 1 : frequency === "custom" ? daysOf(everyDays) : frequency === "monthly" ? 30 : 7;
  return days === 30 ? line : `${line} (~${Math.round((total * 30) / days).toLocaleString()} a month)`;
};

/** First letter up: "using AI for sales" → "Using AI for sales". */
export const capitalised = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** "Weekly", "Every 3 days": a saved signal's checkEveryDays. */
export const everyLabel = days => (days === 1 ? "Daily" : days === 7 ? "Weekly" : days === 30 ? "Monthly" : `Every ${days} days`);

/** {definition, target, frequency, shareable} of a saved custom row; null-safe (the server sends null). */
export const customOf = row => {
  const c = (row && row.custom) || {};
  return {
    definition: c.definition || {},
    target: c.target || {},
    frequency: c.frequency || "weekly",
    everyDays: c.everyDays || 0,
    shareable: c.shareable === true
  };
};

/** A custom feed entry's payload, null-safe: what fired, the quotes behind it and who to reach. */
export const customEventOf = item => {
  const e = (item && item.event) || {};
  const p = e.payload || {};
  const evidence = (Array.isArray(p.evidence) ? p.evidence : []).filter(x => x && x.text);
  const who = p.whoToReach || {};
  const entity = (item && item.entity) || {};
  return {
    signalName: (item && item.signalName) || p.signalName || (item && item.savedSignalName) || "Custom signal",
    why: p.why || e.headline || "",
    evidence: evidence.length ? evidence : p.quote || e.detail ? [{ text: p.quote || e.detail }] : [],
    confidence: typeof p.confidence === "number" ? p.confidence : null,
    recordType: p.recordType || (e.entityType === "ACCOUNT" ? "account" : "contact"),
    recordName: displayName(entity.name, entity.companyName, entity.accountName, p.accountName, item && item.entityName, p.recordName),
    recordDomain: p.recordDomain || (item && item.entityDomain) || "",
    whoToReach: who.persona || who.name ? { persona: who.persona || who.title || "", name: who.name || "", title: who.title || "", linkedinUrl: who.linkedinUrl || "" } : null
  };
};

/** "Jane Doe, VP Revenue Operations" or just the title (names only come for executives). */
export const authorLine = ev => [ev && ev.authorName, ev && ev.authorTitle].filter(Boolean).join(", ");

export const confidenceText = c => (c == null ? "" : c >= 0.8 ? "Strong match" : c >= 0.5 ? "Likely match" : "Possible match");
